/**
 * Users API Schemas
 *
 * Pure Zod schemas for API request/response validation.
 * DB types come from @aica/database repositories.
 *
 * SINGLE SOURCE OF TRUTH for API contract.
 */

import { z } from 'zod/v4';
import { searchTruncatedToSchema } from './common';
import { subscriptionTierSchema } from './stripe';
import { weekStartsOnSchema } from './settings';
import { accessGrantAssignmentSchema, isAccessGrantBaseRole } from './access';

// ============================================================================
// Enums
// ============================================================================

/**
 * User Role Hierarchy (Organization Level)
 *
 * Organization-wide roles with distinct permission sets:
 * - client: Organization owner, full access to all features and billing (highest)
 * - admin: Full administrative access, can manage users and projects
 * - supervisor: Can manage team members, review analyses, access dashboards
 * - agent: Can create records and view own performance, limited project access
 *
 * Permission inheritance:
 * - client/admin: Implicit admin role on all projects
 * - supervisor: Can view all records, manage annotations/reviews
 * - agent: Can only see own records unless explicitly granted project access
 *
 * NOTE: Project-level roles (userProjectRoleSchema) can grant additional
 * permissions within specific projects, independent of org role.
 */
export const userRoleSchema = z.enum(['client', 'admin', 'agent', 'supervisor']);

/**
 * Invite Status Workflow
 *
 * Lifecycle of user invitations:
 * - pending: Invite sent, awaiting user acceptance
 * - accepted: User clicked invite link and completed signup
 * - expired: Invite exceeded expiration time (typically 7 days)
 *
 * Status transitions:
 * - pending → accepted (user accepts invite)
 * - pending → expired (time limit exceeded)
 * - No transitions from accepted/expired states
 *
 * Expired invites can be re-sent, creating a new invite record.
 * Pending invites create placeholder users with clerkUserId = 'pending-{inviteId}'.
 */
export const inviteStatusSchema = z.enum(['pending', 'accepted', 'expired']);

// ============================================================================
// Common Schemas
// ============================================================================

export const userIdParamSchema = z.object({
  id: z.string(),
});

// ============================================================================
// List Users Schemas
// ============================================================================

/**
 * Query params for GET /api/v1/users
 */
export const listUsersQuerySchema = z.object({
  role: userRoleSchema.optional(),
  // Same shape as the invite request's `orgId` below, and for the same reason:
  // this names another organisation to act in, and it reaches a permission
  // check and a repository filter. An unvalidated identifier here is the
  // pattern that let a malformed org id travel all the way to the resolver on
  // the feature-flags routes. Request inputs only — the response schemas in
  // this file keep `z.string()`, because a response schema that rejects what
  // the server actually sent discards the whole payload.
  orgId: z.string().uuid().optional(),
  search: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});

// ============================================================================
// Create/Update User Schemas
// ============================================================================

export const createUserRequestSchema = z
  .object({
    email: z.string().email(),
    name: z.string().min(1),
    role: userRoleSchema.default('agent'),
  })
  .strict();

export const updateUserRequestSchema = z
  .object({
    name: z.string().min(1).optional(),
    role: userRoleSchema.optional(),
    isActive: z.boolean().optional(),
  })
  .strict();

export const inviteUserRequestSchema = z
  .object({
    email: z.string().email(),
    name: z.string().min(1).max(100).optional(), // Optional - will use email prefix if not provided
    role: userRoleSchema,
    orgId: z.string().uuid().optional(), // If not provided, use current user's org
    externalId: z.string().min(1).optional(), // CRM User ID (e.g. "42" from Bitrix24)
    /**
     * Projects a supervisor oversees. Required for `supervisor`, rejected for
     * every other role — and for an invite with `accessGrant` (below), which
     * invites a supervisor without projects.
     *
     * A supervisor's access to record CONTENTS (transcript, analysis) resolves
     * through `project_users` — see `record.read` / `analysis.read` in
     * `packages/core/src/rbac/permissions.ts`. An invite that skipped this
     * produced a supervisor who could list calls but could not open a single
     * one, which is exactly how production ended up with three supervisors and
     * zero assignments.
     *
     * This closes the INVITE path only. Promoting an existing user through
     * `PATCH /users/:id/role` still lands them at zero projects — that route
     * takes a role and nothing else. What catches it there is the team list,
     * which badges a supervisor holding no projects as a warning rather than a
     * neutral count, one click from the fix. So the broken state is reachable,
     * and visible when it happens; it is no longer reachable SILENTLY.
     *
     * Not asked of anyone else on purpose: client/admin are org-wide by role,
     * and an agent is bounded by the record they handled — project membership
     * is not what scopes them.
     */
    projectIds: z.array(z.string().uuid()).optional(),
    /**
     * Invite WITH a narrow analytics access (E1.5 PR 5b, #1336; owner
     * decision 5: «базовая роль — `supervisor` без проектов»). The same body
     * `PUT /users/:id/access` takes; the membership and the grant are written
     * in ONE batch, so the invitee never exists without the grant. Only on a
     * `supervisor` or an `agent` (owner decision 05.10), and with no projects:
     * under the grant projects widen nothing, and a revoked grant leaves the
     * person at the role alone — «почти ничего не видит».
     *
     * Gated by `enableAccessGrants` — tier and flag, see the catalog note of
     * `access.analytics_grants` — and asks `user.manage_access` on top of the
     * invite right. `.strict()` rejects this field on an edge-api that predates
     * it. Rolling edge-api back alone is safe: an older edge-api reports the
     * flag as off, so the UI hides the option and sends nothing; only a flag
     * map cached by a UI loaded before the rollback still sends it and gets a 403.
     */
    accessGrant: accessGrantAssignmentSchema.optional(),
  })
  .strict()
  .superRefine((data, ctx) => {
    if (data.accessGrant) {
      if (!isAccessGrantBaseRole(data.role)) {
        ctx.addIssue({
          code: 'custom',
          path: ['accessGrant'],
          message: 'An access grant can be given only to a Supervisor or an Agent',
        });
      }
      if (data.projectIds && data.projectIds.length > 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['projectIds'],
          message: 'A member invited with an access grant is invited without projects',
        });
      }
      return;
    }

    if (data.role === 'supervisor') {
      if (!data.projectIds || data.projectIds.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['projectIds'],
          message: 'A supervisor must be assigned at least one project',
        });
      }
      return;
    }

    if (data.projectIds && data.projectIds.length > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['projectIds'],
        message: 'Project assignments apply to supervisors only',
      });
    }
  });

// ============================================================================
// User Management Schemas (for specific field updates)
// ============================================================================

/**
 * Update User Role
 *
 * Change user's organization-level role via PATCH /api/v1/users/:id/role.
 * Only admins/clients can perform role changes.
 *
 * Role changes affect:
 * - Global permissions across all projects
 * - Access to admin features (user management, billing, etc.)
 * - Default project access (admins get implicit access to all projects)
 */
export const updateUserRoleSchema = z.object({
  role: userRoleSchema,
});

/**
 * Update User Profile
 *
 * Modify user's personal information via PATCH /api/v1/users/:id/profile.
 * Users can update their own profile; admins can update any user.
 *
 * Fields:
 * - name: Display name shown in UI and notifications
 * - avatarUrl: Profile picture URL (nullable to allow removal)
 */
export const updateUserProfileSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  avatarUrl: z.string().url().nullable().optional(),
});

/**
 * Project Role (Project-Level Permissions)
 *
 * Permissions within a specific project, independent of org-level role:
 * - admin: Manage project settings, channels, integrations, users
 * - member: Create records, run analyses, add annotations
 * - viewer: Read-only access to records and analyses
 *
 * Distinction from userRoleSchema (org-level):
 * - userRoleSchema: Organization-wide role affecting all projects
 * - userProjectRoleSchema: Project-specific role for granular access control
 *
 * Example: User can be org 'viewer' but project 'admin' for specific projects.
 * Org-level admins/clients have implicit 'admin' role on all projects.
 */
export const userProjectRoleSchema = z.enum(['admin', 'member', 'viewer']);

/**
 * Update a user's project oversight — PATCH /api/v1/users/:id/projects.
 *
 * Ids only, no role: this request grants and revokes OVERSIGHT, which is the
 * one thing project membership decides (a supervisor's reach over record
 * contents — `record.read` / `analysis.read`). The mirror request from the
 * project side, `updateProjectSupervisorsRequestSchema`, carries `userIds` for
 * the same reason.
 *
 * The role used to travel here, and it was the only asymmetry between the two
 * screens: this one could promote someone to project 'admin' and the project's
 * own Team tab could not, so the same person became unmanageable there
 * depending on a checkbox set here. A project-'admin' row now means one thing —
 * the creator administering what they made — and neither screen writes it.
 */
export const updateUserProjectsSchema = z.object({
  projectIds: z.array(z.string().uuid()),
});

// ============================================================================
// User Response Schemas
// ============================================================================

/**
 * User list item - matches API response from GET /api/v1/users
 * This is the structure returned in the list endpoint
 */
export const userListItemSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: userRoleSchema,
  // Platform owner (users.is_owner). Such users have no org role of their own,
  // so the team list must label them "Owner" instead of falling back to their
  // membership role (which reads as "Client").
  isOwner: z.boolean().optional(),
  clerkUserId: z.string().optional(), // For checking pending invites (starts with 'pending-')
  orgName: z.string().nullable(),
  projectCount: z.number().int(),
  createdAt: z.string(),
});

export const listUsersResponseSchema = z.object({
  users: z.array(userListItemSchema),
  total: z.number().int(),
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
  /** Запрос пришлось укоротить под предел D1 — см. `searchTruncatedToSchema`. */
  searchTruncatedTo: searchTruncatedToSchema,
  /**
   * Сколько в организации ПРИНЯТЫХ Клиентов — без приглашённых, ещё не
   * вошедших, и без владельца платформы, — независимо от фильтров и страницы
   * (#877): интерфейс не предлагает понизить или удалить последнего. Сервер
   * присылает его только Клиенту и владельцу платформы: админу все Клиенты
   * закрыты по роли. Необязательное — старый воркер его не присылает.
   */
  clientCount: z.number().int().optional(),
});

/**
 * Код ответа 409 на смену роли или удаление, после которых в организации не
 * осталось бы роли «Клиент» (#877). Интерфейс по нему показывает свой текст.
 */
export const LAST_CLIENT_ERROR_CODE = 'last_client';

/**
 * Код ответа 409, когда роль участника изменилась между проверкой прав и
 * записью: право проверялось на старую роль (#877). Интерфейс просит обновить.
 */
export const ROLE_CHANGED_ERROR_CODE = 'role_changed';

/**
 * Код ответа 409 на смену роли на `client`/`admin` человеку с действующим
 * грантом доступа (решение владельца 05.10, #1336): грант живёт только поверх
 * `supervisor`/`agent` (`ACCESS_GRANT_BASE_ROLES`). Сначала отозвать грант.
 * Проверяется в том же запросе, что и запись роли, — ничего не записано.
 */
export const GRANT_BLOCKS_ROLE_ERROR_CODE = 'grant_blocks_role';

/**
 * User detail - matches API response from GET /api/v1/users/:id
 * This is the structure returned in the detail endpoint
 */
export const userResponseSchema = z.object({
  id: z.string(),
  clerkUserId: z.string().optional(),
  email: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  orgId: z.string().optional(),
  role: userRoleSchema.optional(),
  orgName: z.string().nullable(),
  projectCount: z.number().int().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export const getUserResponseSchema = z.object({
  user: userResponseSchema,
});

export const createUserResponseSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  email: z.string(),
  name: z.string(),
  role: userRoleSchema,
  createdAt: z.string(),
});

// Legacy alias for backwards compatibility
export const userWithRoleSchema = userResponseSchema;

// ============================================================================
// Current User (Me) Schemas
// ============================================================================

/**
 * Current User (Me) Response
 *
 * Extended user profile from GET /api/v1/users/me including multi-org context.
 *
 * Multi-organization support:
 * - organizations: List of all orgs this user belongs to with their roles.
 *   Enables org-switcher UI and cross-org navigation.
 * - currentOrg: Active organization for current session, determined by:
 *   1. X-Organization-Id header (if provided)
 *   2. Last accessed org (from user preferences)
 *   3. First org in organizations array (fallback)
 *   Null if user has no org memberships (shouldn't happen in normal flow).
 *
 * Use cases:
 * - Display user info in nav bar
 * - Populate org switcher dropdown
 * - Determine current permissions based on role in currentOrg
 * - Show onboarding if organizations is empty
 */
/**
 * Current-user response — matches GET /api/v1/users/me
 * (apps/edge-api/src/routes/users/me.ts). Flat shape: the user fields plus the
 * full org roster with the user's role in each.
 */
export const meResponseSchema = z.object({
  userId: z.string(),
  clerkUserId: z.string(),
  email: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  isOwner: z.boolean(),
  /**
   * Состояние поп-апа «что нового» (#946): последний увиденный релиз и момент
   * появления пользователя. `.optional()` — чтобы фронт новой версии не падал
   * на ответе воркера, который ещё не выкатился: `/me` — первый запрос после
   * входа, и жёсткое требование поля означало бы пустой экран на время
   * рассинхрона деплоев.
   */
  lastSeenRelease: z.string().nullable().optional(),
  userCreatedAt: z.string().nullable().optional(),
  orgs: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      role: userRoleSchema,
      bitrix24Domain: z.string().nullable(),
      /**
       * Тариф организации (#978). Едет здесь, потому что иначе клиенту его не
       * узнать: подписка запрашивается только при `billing.read`, а у admin,
       * agent и supervisor его нет.
       *
       * `.optional()` — чтобы фронт новой версии не падал на ответе воркера,
       * который ещё не выкатился: `/me` — первый запрос после входа, и
       * жёсткое требование поля означало бы пустой экран на время рассинхрона.
       */
      subscriptionTier: subscriptionTierSchema.optional(),
      /**
       * Первый день недели организации (#864), ISO 1 = пн … 7 = вс, уже
       * приведённый (`readWeekStartsOn`: не выбран или битый — понедельник).
       * Едет здесь, потому что `GET /api/v1/settings` — только client/admin,
       * а неделю на дашборде режут все роли. Только это поле настроек, не
       * остальные.
       *
       * `.optional()` — по той же причине, что `subscriptionTier`: ответ
       * воркера старше поля не должен ронять первый запрос после входа.
       */
      weekStartsOn: weekStartsOnSchema.optional(),
    })
  ),
});

// ============================================================================
// Invite Schemas
// ============================================================================

/**
 * Invite Response
 *
 * User invitation details from GET /api/v1/invites or GET /api/v1/invites/:id.
 *
 * Invite workflow:
 * 1. Admin creates invite via POST /api/v1/invites (sets role, email, expiration)
 * 2. System sends email with magic link containing invite token
 * 3. User clicks link, redirected to signup flow with pre-filled email
 * 4. On signup completion, invite status → 'accepted', acceptedAt timestamp set
 * 5. If not accepted before expiresAt, background job marks as 'expired'
 *
 * Fields:
 * - expiresAt: ISO timestamp when invite becomes invalid (typically 7 days)
 * - acceptedAt: When user completed signup (null if still pending/expired)
 * - invitedBy: Admin who created the invite (for audit trail)
 *   Nullable if inviter was deleted or for system-generated invites.
 *
 * Pending invites create placeholder user records with:
 * - clerkUserId = 'pending-{inviteId}'
 * - email, name, role pre-populated
 * - Converted to real user on acceptance
 */
export const inviteResponseSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  email: z.string(),
  role: userRoleSchema,
  status: inviteStatusSchema,
  expiresAt: z.string(),
  acceptedAt: z.string().nullable(),
  createdAt: z.string().nullable(),
  invitedBy: z
    .object({
      id: z.string(),
      name: z.string(),
      email: z.string(),
    })
    .nullable(),
});

export const listInvitesResponseSchema = z.object({
  invites: z.array(inviteResponseSchema),
  total: z.number().int(),
});

export const createInviteResponseSchema = z.object({
  invite: inviteResponseSchema,
  message: z.string().optional(),
});

// ============================================================================
// Type exports (inferred from schemas)
// ============================================================================

export type UserRole = z.infer<typeof userRoleSchema>;
export type InviteStatus = z.infer<typeof inviteStatusSchema>;

export type ListUsersQuery = z.infer<typeof listUsersQuerySchema>;
export type CreateUserRequest = z.infer<typeof createUserRequestSchema>;
export type UpdateUserRequest = z.infer<typeof updateUserRequestSchema>;
export type InviteUserRequest = z.infer<typeof inviteUserRequestSchema>;
export type UpdateUserRole = z.infer<typeof updateUserRoleSchema>;
export type UpdateUserProfile = z.infer<typeof updateUserProfileSchema>;
export type UserProjectRole = z.infer<typeof userProjectRoleSchema>;
export type UpdateUserProjects = z.infer<typeof updateUserProjectsSchema>;

export type UserListItem = z.infer<typeof userListItemSchema>;
export type UserResponse = z.infer<typeof userResponseSchema>;
export type UserWithRole = z.infer<typeof userWithRoleSchema>;
export type CreateUserResponse = z.infer<typeof createUserResponseSchema>;
export type InviteResponse = z.infer<typeof inviteResponseSchema>;
export type MeResponse = z.infer<typeof meResponseSchema>;
